// GlobalErrorListener — подписан на API-события из axios interceptor.
// Показывает toast'ы для не-401 ошибок, делает navigate на /login для 401.
//
// Должен рендериться ВНУТРИ <AntApp>, чтобы получить message API через useApp().
import { useEffect } from "react";
import { App as AntApp } from "antd";
import { useNavigate } from "react-router-dom";
import { API_EVENTS, type APIErrorEventDetail } from "@/api/client";
import { useCrossTabLogout } from "@/hooks/useCrossTabLogout";

export function GlobalErrorListener() {
  const { message } = AntApp.useApp();
  const navigate = useNavigate();
  // Подписка на cross-tab logout signal — компонент уже внутри Router context.
  useCrossTabLogout();

  useEffect(() => {
    const onApiError = (event: Event) => {
      const detail = (event as CustomEvent<APIErrorEventDetail>).detail;
      if (!detail) return;
      const { error } = detail;

      // 401 обрабатывается отдельным событием unauthorized.
      // 423 / 429: toast с retry_after_seconds, если бэкенд прислал.
      let text = error.message;
      if ((error.status === 423 || error.status === 429) && error.retry_after_seconds) {
        const secs = error.retry_after_seconds;
        text = `${error.message} (попробуйте через ~${Math.ceil(secs / 60)} мин)`;
      }

      if (error.status >= 500 || error.status === 502 || error.status === 504) {
        message.error(text);
      } else if (error.status === 403) {
        message.warning(text);
      } else if (error.status === 423 || error.status === 429) {
        message.warning(text);
      } else {
        // 400/404/409/422 — обычно показываются на месте формы. Глобальный toast
        // только когда компонент сам не отрисовал ошибку — чтобы не дублировать.
        // Текущая стратегия: всегда показывать, чтобы пользователь видел сигнал.
        message.error(text);
      }
    };

    const onUnauthorized = (event: Event) => {
      const detail = (event as CustomEvent<{ returnTo: string }>).detail;
      const returnTo = detail?.returnTo ?? "/";
      // Только если мы НЕ на /login (избегаем циклов).
      if (!window.location.pathname.startsWith("/login")) {
        navigate("/login", { replace: true, state: { returnTo } });
      }
    };

    window.addEventListener(API_EVENTS.error, onApiError);
    window.addEventListener(API_EVENTS.unauthorized, onUnauthorized);

    return () => {
      window.removeEventListener(API_EVENTS.error, onApiError);
      window.removeEventListener(API_EVENTS.unauthorized, onUnauthorized);
    };
  }, [message, navigate]);

  return null;
}
